import AppDispatcher from '../dispatcher/AppDispatcher';
import { EventEmitter } from 'events';
import { CHANGE_EVENT } from '../constants/AppConstants';
import AccountPoolsConstant from '../constants/AccountPoolsConstant';
import AccountPoolsApiUtils from '../utils/AccountPoolsApiUtils';
import FavouritesConstants from '../constants/FavouritesConstants';
import CurrencyUtils from '../utils/CurrencyUtils';
import AccountConstants from '../constants/AccountConstants';
import { isEnrichCapabilityAvailable } from '../utils/AccountUtils';

let accountPoolsLoaded = false;
let submitPool = false;
let accountPools = null;
let selectedPoolId = null;
let accounts = [];
let enrichedAccountIds = null;
let fetchAccounts = false;
let specificPoolDetails = {};
let editMode = false;
let isPoolUpdated = false;
let isSpecificPool = false;
let downloadFile = null;
let fileDownloadError = false;
let isAccountDetailsLoaded = false;
class AccountPoolsStore extends EventEmitter {
  constructor() {
    super();
    this.dispatchToken = AppDispatcher.register(
      this.dispatcherCallback.bind(this)
    );
  }
  emitChange() {
    this.emit(CHANGE_EVENT);
  }

  addChangeListener(callback) {
    this.on(CHANGE_EVENT, callback);
  }

  removeChangeListener(callback) {
    this.removeListener(CHANGE_EVENT, callback);

    enrichedAccountIds = null;
    isSpecificPool = false;
  }
  getAccountPools() {
    return accountPools;
  }

  isAccountPoolsLoaded() {
    return accountPoolsLoaded;
  }

  isSpecificPoolDetailsLoaded() {
    return isSpecificPool;
  }

  isAccountLoaded() {
    return fetchAccounts;
  }

  isPoolSubmitted() {
    return submitPool;
  }

  isPoolUpdated() {
    return isPoolUpdated;
  }

  getAllAccounts() {
    return accounts || [];
  }

  getSelectedAccountPoolId() {
    return selectedPoolId;
  }

  returnCSV() {
    return downloadFile;
  }

  accountDetailsLoadedFlag() {
    return isAccountDetailsLoaded;
  }

  getPoolDetails() {
    if (!specificPoolDetails.pools || specificPoolDetails.pools.length === 0)
      return null;
    const {
      available_balance,
      current_balance,
      current_cleared_balance,
      previous_day_balance,
      currency,
      accounts,
      name,
    } = specificPoolDetails.pools[0];
    const accountList = this.getAccountListFromPool(accounts);
    return {
      totalValues: [
        CurrencyUtils.penceToPounds(previous_day_balance),
        CurrencyUtils.penceToPounds(current_balance),
        CurrencyUtils.penceToPounds(current_cleared_balance),
        CurrencyUtils.penceToPounds(available_balance),
      ],
      availableBalance: CurrencyUtils.penceToPounds(available_balance),
      currentBalance: CurrencyUtils.penceToPounds(current_balance),
      clearedBalance: CurrencyUtils.penceToPounds(current_cleared_balance),
      lastBusinessDayBalance: CurrencyUtils.penceToPounds(previous_day_balance),
      currencySymbol: currency,
      accounts: accountList,
      accountCount: accountList.length,
      poolName: name,
    };
  }
  getSelectedAccountIds() {
    const selectedAccountIds = [];
    const { accounts: poolAccounts } = specificPoolDetails.pools[0];
    poolAccounts.forEach((poolAccount) => {
      const account = accounts.find((account) => account.id === poolAccount.id);
      if (account) {
        selectedAccountIds.push(account.id);
      }
    });
    return selectedAccountIds;
  }
  getAccountListFromPool(accounts) {
    return accounts.map(
      ({
        id,
        currency,
        previous_day_balance,
        current_cleared_balance,
        available_balance,
        current_balance,
      }) => {
        return {
          currencySymbol: currency,
          lastBusinessDayBalance:
            CurrencyUtils.penceToPounds(previous_day_balance),
          clearedBalance: CurrencyUtils.penceToPounds(current_cleared_balance),
          availableBalance: CurrencyUtils.penceToPounds(available_balance),
          currentBalance: CurrencyUtils.penceToPounds(current_balance),
          accountId: id,
        };
      }
    );
  }

  getEditPoolDetails() {
    if (!specificPoolDetails || specificPoolDetails.pools.length === 0)
      return null;
    const { name, currency } = specificPoolDetails.pools[0];
    return {
      poolName: name,
      currency: currency,
      accounts: this.getSelectedAccountIds(),
      poolId: selectedPoolId,
    };
  }

  getEnrichedAccountIdsFromPool = () => {
    if (Array.isArray(enrichedAccountIds)) {
      return enrichedAccountIds;
    }

    enrichedAccountIds = accounts.reduce((accumulator, account) => {
      if (isEnrichCapabilityAvailable(account)) {
        accumulator.push(account.id);
      }

      return accumulator;
    }, []);

    return enrichedAccountIds;
  };

  isEditMode() {
    return editMode;
  }

  returnDownloadError() {
    return fileDownloadError;
  }

  dispatcherCallback({ action }) {
    const actionType = action.actionType;
    const actionData = action.data;
    switch (actionType) {
      case AccountPoolsConstant.GET_ACCOUNT_POOLS:
        accountPoolsLoaded = false;
        AccountPoolsApiUtils.getAccountPools();
        break;
      case AccountPoolsConstant.GET_ACCOUNT_POOLS_SUCCESS:
        accountPoolsLoaded = true;
        accountPools = actionData;
        this.emitChange();
        break;
      case AccountPoolsConstant.GET_ACCOUNT_POOLS_FAILURE:
        accountPoolsLoaded = false;
        this.emitChange();
        break;
      case AccountPoolsConstant.SET_ACCOUNT_POOL_ID:
        selectedPoolId = actionData;
        this.emitChange();
        break;
      case AccountPoolsConstant.CREATE_ACCOUNT_POOL:
        submitPool = false;
        AccountPoolsApiUtils.createAccountPool(actionData);
        this.emitChange();
        break;
      case AccountPoolsConstant.CREATE_ACCOUNT_POOL_SUCCESS:
        submitPool = true;
        accountPools = actionData;
        this.emitChange();
        break;
      case AccountPoolsConstant.CREATE_ACCOUNT_POOL_FAILURE:
        submitPool = false;
        this.emitChange();
        break;
      case AccountPoolsConstant.GET_ACCOUNT_LIST:
        fetchAccounts = false;
        AccountPoolsApiUtils.getAccountsList();
        this.emitChange();
        break;
      case AccountPoolsConstant.GET_ACCOUNT_LIST_SUCCESS:
        fetchAccounts = true;
        const newAccounList = actionData.accounts.sort((a, b) =>
          a.metadata.display_order > b.metadata.display_order ? 1 : -1
        );
        accounts = newAccounList;
        this.emitChange();
        break;
      case AccountPoolsConstant.GET_ACCOUNT_LIST_FAILURE:
        fetchAccounts = false;
        this.emitChange();
        break;
      case AccountPoolsConstant.RESET_POOL:
        submitPool = false;
        isPoolUpdated = false;
        editMode = false;
        this.emitChange();
        break;
      case AccountPoolsConstant.UPDATE_POOL:
        isPoolUpdated = false;
        AccountPoolsApiUtils.updateAccountPool(selectedPoolId, actionData);
        this.emitChange();
        break;
      case AccountPoolsConstant.UPDATE_POOL_SUCCESS:
        isPoolUpdated = true;
        this.emitChange();
        break;
      case AccountPoolsConstant.UPDATE_POOL_FAILURE:
        isPoolUpdated = false;
        this.emitChange();
        break;
      case AccountPoolsConstant.DELETE_ACCOUNT_POOL:
        AccountPoolsApiUtils.deleteAccountPool(selectedPoolId);
        break;
      case AccountPoolsConstant.GET_SPECIFIC_POOL_DETAIL:
        AccountPoolsApiUtils.getSpecificPoolDetail(selectedPoolId);
        isSpecificPool = false;
        this.emitChange();
        break;
      case AccountPoolsConstant.GET_SPECIFIC_POOL_DETAIL_SUCCESS:
        isSpecificPool = true;
        specificPoolDetails = actionData;
        this.emitChange();
        break;
      case AccountPoolsConstant.GET_SPECIFIC_POOL_DETAIL_FAILURE:
        isSpecificPool = false;
        this.emitChange();
        break;
      case AccountPoolsConstant.SET_EDIT_MODE:
        editMode = true;
        this.emitChange();
        break;
      case AccountPoolsConstant.EXPORT_TRANSACTIONS:
        AccountPoolsApiUtils.exportTrasnactions(actionData);
        break;
      case FavouritesConstants.REQUEST_ACCOUNT_DETAILS_SUCCESS:
        isAccountDetailsLoaded = isSpecificPool;
        this.emitChange();
        break;
      case AccountConstants.REQUEST_ACCOUNT_DETAILS_SUCCESS:
        isAccountDetailsLoaded = isSpecificPool;
        this.emitChange();
        break;
      case AccountPoolsConstant.RESET_ACCOUNT_DETAIL_FLAG:
        isAccountDetailsLoaded = false;
        this.emitChange();
        break;
      default: {
        break;
      }
    }
  }
}

export default new AccountPoolsStore();
